/**
 * Where the "New" marker sits — the latch rule, as a calculation.
 *
 * Deliberately the OPPOSITE of the per-message dot. A dot asks "is this past my cursor
 * right now" and must stay live, so mark-read-on-view puts it out seconds later (the
 * reasoning is in `unread-boundary.ts`). A marker asks "where did I leave off when I
 * opened this", which is a question about the VISIT, not about the current cursor — a
 * marker that followed the cursor would vanish the instant the viewer looked at the thread
 * they just opened, and jump around as they kept chatting.
 *
 * So the boundary is latched the first time it can be computed for a cid and held until
 * the cid changes. Switching conversations is the only thing that clears it, which is the
 * behavior Slack has and what the marker is for.
 *
 * Latching on the first ANSWER rather than on mount is what makes it correct: the read
 * cursor (`GET /:cid/unread`), the viewer (`auth/me`) and the message page are three
 * independent queries, so the thread renders several times before an unread decision is
 * possible at all. Mount is simply too early to ask.
 *
 * Known edge: the boundary is the oldest unread in the LOADED window. A thread opened with
 * more unread messages than one page holds latches part-way down, and scrolling further
 * back does not move the marker — it stays where it was first drawn, which is the stable
 * answer even if it is not the complete one.
 */

import {
  isMessageUnread,
  type UnreadContext,
  type UnreadMessage,
} from "./unread-boundary";

/**
 * `messageId: null` is the resolved-empty answer — the viewer opened this cid already
 * caught up — and is deliberately sticky, so a message arriving mid-visit does not draw a
 * marker above itself while the viewer is watching it land.
 */
export type NewDividerLatch = { cid: string; messageId: string | null };

/**
 * The decision's inputs have actually answered for THIS cid.
 *
 * `unreadSettled` is the load-bearing half, and it is not the same question as "is
 * `lastReadMessageId` defined". The query client serves cached data on mount
 * (`staleTime` 60s, `refetchOnMount: true`), so the first render of a reopened
 * conversation carries the read state from the PREVIOUS visit — which says "caught up"
 * — and only revalidates a moment later. Latching the empty answer off that reads a
 * stale cache as a server answer and suppresses the marker for the whole visit.
 *
 * `viewerRoles` is still checked here because `auth/me` is a separate query that
 * `unreadSettled` says nothing about, and without it `isMessageUnread` withholds every
 * message — see its own readiness guard.
 */
function isDecidable(
  { viewerRoles, lastReadMessageId }: UnreadContext,
  unreadSettled: boolean,
): boolean {
  return (
    unreadSettled &&
    viewerRoles !== undefined &&
    lastReadMessageId !== undefined
  );
}

export function resolveNewDividerLatch<TMessage extends UnreadMessage>(
  current: NewDividerLatch | null,
  cid: string,
  /** Oldest-first, so the first match is the boundary. */
  messages: readonly TMessage[],
  context: UnreadContext,
  /** `GET /:cid/unread` has answered for this cid and is not revalidating. */
  unreadSettled: boolean,
): NewDividerLatch | null {
  // Switching conversations is the ONLY reset — this view is not always remounted per cid,
  // so the marker has to be dropped here rather than relying on a `key`.
  const latched = current?.cid === cid ? current : null;
  if (latched) return latched;

  // An empty list is "the page has not arrived", not "nothing is unread" — resolving here
  // would latch empty before the thread had a chance to hold an unread message at all.
  if (messages.length === 0 || !isDecidable(context, unreadSettled))
    return null;

  const firstUnread = messages.find((message) =>
    isMessageUnread(message, context),
  );
  return { cid, messageId: firstUnread?.id ?? null };
}
